import { SearchIcon, SmallCloseIcon } from "@chakra-ui/icons";
import { Input, InputGroup, InputLeftElement, InputRightElement, Spinner } from "@chakra-ui/react";
import { forwardRef } from "react";
import { pageWidth } from "./App";
import { DropDown } from "./DropDown";
import { SelectorOption } from "./GenerateSelectorOptions";
import { useAppContext } from "./AppContext";

type InputProps = {
  errorMsgs: string[];
  verse: string;
  isSuggestionsOpen: boolean;
  setIsSuggestionsOpen: (isOpen: boolean) => void;
  isLoading: boolean;
  options: SelectorOption[];
  searchHistory: string[];
  isHistoryOpen: boolean;
  setIsHistoryOpen: (isOpen: boolean) => void;
  setVerse: (value: string) => void;
};

export const InputComponents = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    errorMsgs,
    verse,
    isSuggestionsOpen,
    setIsSuggestionsOpen,
    isLoading,
    options,
    searchHistory,
    isHistoryOpen,
    setIsHistoryOpen,
    setVerse,
  } = props;
  const INPUTHEIGHT = "45px";
  const { isEnglish, query, handleChange, handleSubmit, versions } = useAppContext();
  const focusOnInput = () => {
    // set timeout make sure focus was excuted ontime
    setTimeout(() => {
      if (ref && typeof ref !== "function" && ref.current) {
        ref.current.focus();

        // Set cursor to the end of the input
        const inputValue = ref.current.value;
        ref.current.setSelectionRange(inputValue.length, inputValue.length);
      }
    }, 10);
  };

  return (
    <InputGroup w={pageWidth}>
      <InputLeftElement pointerEvents="none" h={INPUTHEIGHT} children={<SearchIcon color="gray.400" />} />

      <Input
        paddingInline={"40px"}
        w={pageWidth}
        ref={ref}
        h={INPUTHEIGHT}
        isInvalid={errorMsgs.includes(verse)}
        errorBorderColor="crimson"
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isSuggestionsOpen && !isHistoryOpen) {
            handleSubmit(versions, query);
          }
        }}
        type="text"
        value={query}
        placeholder={isEnglish ? "Input format: Mark 1 1 3 or Mark1:1-3" : "請輸入經文如 創 1 1 3 or 創1:1-3"}
        enterKeyHint="search"
        onFocus={() => {
          setIsHistoryOpen(query.length === 0 && searchHistory.length > 0);
          setIsSuggestionsOpen(query.length > 0 && options.length > 0);
        }}
        onBlur={() => {
          setIsSuggestionsOpen(false);
          setIsHistoryOpen(false);
        }}
      />

      {query.length > 0 && (
        <DropDown
          isMenuOpen={isSuggestionsOpen}
          setIsMenuOpen={setIsSuggestionsOpen}
          options={options}
          onClickfocus={focusOnInput}
          handleChange={handleChange}
        />
      )}
      <DropDown
        isMenuOpen={isHistoryOpen}
        setIsMenuOpen={setIsHistoryOpen}
        options={searchHistory.map((query) => ({ label: query, value: query })).reverse()}
        onClickfocus={focusOnInput}
        handleChange={handleChange}
        isHistory
      />
      <InputRightElement
        h={INPUTHEIGHT}
        children={
          <>
            {isLoading && <Spinner size="sm" />}
            <SmallCloseIcon
              w={"20px"}
              h={"20px"}
              onClick={() => {
                handleChange({ target: { value: "" } });
                setVerse("");
                focusOnInput();
              }}
              color="gray.400"
              _hover={{ color: "gray.600" }}
            />
          </>
        }
      />
    </InputGroup>
  );
});
